import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as hooks from '@owl-nest/hooks'

import * as cold from '@owl-nest/cold-data/client'
import * as utils from '@owl-nest/utils'
import * as url from '@boutique/url'

import { useControls } from '../hooks/useControls'
import { useProductListing } from '../hooks/useProductListing'

import { ArrayFilter } from './ArrayFilter'
import { PriceFilter } from './PriceFilter'
import { BooleanFilter } from './BooleanFilter'

export function Filters(): React.ReactElement {
  const [isFiltersOpen, setIsFiltersOpen] = useControls()

  const { resetFilters, filterCount, persistedData, apply, query } = useProductListing()

  const { focusableFilterProps, closeAllFilters } = useFilterFocus()

  return (
    <ResponsiveWrapper isOpen={isFiltersOpen}>
      <HeadWrapper>
        <plume.styles.heading.XS>{`Filtres${filterCount > 0 ? ` (${filterCount})` : ''}`}</plume.styles.heading.XS>
        {filterCount !== 0 && (
          <plume.styles.copy.S>
            <plume.LinkAsButton kind="secondary" underlined onClick={() => apply(resetFilters())}>
              Réinitialiser les filtres
            </plume.LinkAsButton>
          </plume.styles.copy.S>
        )}
        <plume.glyphs.stroke.Close size={16} onClick={() => setIsFiltersOpen(false)} />
      </HeadWrapper>

      <FiltersList>
        <ArrayFilter
          data={persistedData}
          key="types"
          name="types"
          options={Array.from(
            new Set(cold.types['product-type'].flatMap((slug) => cold.taxonomies[slug].children.tags)),
          ).map((slug) => ({
            value: slug,
            name: cold.tags[slug].name,
          }))}
          title="Catégories de produits"
          {...focusableFilterProps('types')}
        />
        {query.facetFilters?.category === 'mode' && (
          <ArrayFilter
            data={persistedData}
            name="genders"
            options={cold.taxonomies[cold.types['gender']].children.tags.map((slug) => ({
              value: slug,
              name: cold.tags[slug].name,
            }))}
            title="Genre"
            {...focusableFilterProps('genders')}
          />
        )}
        <ArrayFilter
          data={persistedData}
          key="sizes"
          name="sizes"
          options={cold.taxonomies[cold.types['size']].children.tags.map((slug) => ({
            value: slug,
            name: cold.tags[slug].name,
          }))}
          title="Tailles"
          {...focusableFilterProps('sizes')}
        />
        <ArrayFilter
          data={persistedData}
          key="colors"
          name="colors"
          options={cold.taxonomies[cold.types['color']].children.tags.map((slug) => ({
            value: slug,
            name: cold.tags[slug].name,
          }))}
          title="Couleurs"
          {...focusableFilterProps('colors')}
        />
        <PriceFilter data={persistedData} />
        <ArrayFilter
          data={persistedData}
          key="brands"
          name="brands"
          options={Object.values(cold.brands).map((brand) => {
            return {
              value: brand.slug,
              facetValue: brand.identifier,
              name: brand.name,
              canonicalUrl: url.products({
                title: '',
                query: {
                  facetFilters: {
                    brands: [brand?.slug],
                  },
                },
              }).path,
            }
          })}
          title="Marques"
          {...focusableFilterProps('brands')}
        />
        <ArrayFilter
          data={persistedData}
          key="environmentalPillars"
          name="environmentalPillars"
          options={cold.taxonomies[cold.types['environmental-pillar']].children.tags.map((item) => ({
            value: item,
            name: cold.tags[item].name,
            canonicalUrl: url.products({
              title: '',
              query: { facetFilters: { environmentalPillars: [item] } },
            }).path,
          }))}
          title="Critères environnementaux"
          {...focusableFilterProps('environmentalPillars')}
        />
        <ArrayFilter
          data={persistedData}
          key="socialPillars"
          name="socialPillars"
          options={cold.taxonomies[cold.types['social-pillar']].children.tags.map((item) => ({
            value: item,
            name: cold.tags[item].name,
            canonicalUrl: url.products({ title: '', query: { facetFilters: { socialPillars: [item] } } }).path,
          }))}
          title="Critères sociaux"
          {...focusableFilterProps('socialPillars')}
        />
        <BooleanFilter
          data={persistedData}
          key="deal"
          name="deal"
          option={{
            name: 'Prix réduits',
            canonicalUrl: url.products({ title: '', query: { facetFilters: { deal: true } } }).path,
          }}
          title="Bons plans"
          {...focusableFilterProps('deal')}
        />
      </FiltersList>

      <SeeSelectionButton
        onClick={() => {
          setIsFiltersOpen(false)
          closeAllFilters()
          document.getElementById('top')?.scrollIntoView()
        }}
      >
        Voir la selection ({persistedData?.nbHits ?? 'X'})
      </SeeSelectionButton>
    </ResponsiveWrapper>
  )
}

function useFilterFocus() {
  const [focus, setFocus] = React.useState('')

  return {
    focusableFilterProps: (name: string) => ({
      open: isOpen(name),
      onToggle: () => toggle(name),
    }),
    closeAllFilters: () => setFocus(''),
  }

  function isOpen(name: string) {
    return name === focus
  }

  function toggle(name: string) {
    setFocus(focus === name ? '' : name)
  }
}

const SeeSelectionButton = styled(plume.Button)`
  align-self: flex-end;
  width: 100%;
  z-index: 1;
  flex: none;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: none;
  }
`

const ResponsiveWrapper = styled.div<{ isOpen: boolean }>`
  background-color: ${plume.COLORS.PRIMARY_WHITE};
  bottom: 0;
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  left: 0;
  padding: 25px;
  position: fixed;
  right: 0;
  top: ${plume.SIZES.HEADER_HEIGHT};
  width: 100%;
  z-index: 100;
  overflow: scroll;

  ${plume.Accordion} {
    margin-bottom: 16px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    position: initial;
    top: initial;
    right: initial;
    left: initial;
    width: initial;
    z-index: initial;
    padding: initial;
    display: initial;
  }
`

const HeadWrapper = styled.div`
  margin-bottom: 15px;
  position: relative;

  ${plume.glyphs.stroke.Close} {
    position: absolute;
    top: 5px;
    right: 0;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      display: none;
    }
  }

  ${plume.LinkAsButton} {
    padding: 0;

    font-weight: 600;
    color: ${plume.COLORS.GREY_SHADE_2};
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`

const FiltersList = styled.ul`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`
